/**
 * Fatiado de pages/ImportMenu.tsx por responsabilidade (#131, R3).
 */
import { useEffect, useRef, useState } from 'react'
import { importsApi } from '../../lib/api.js'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ImportDraft, ImportDraftProduct, MenuImportView } from '../../lib/api.js'
import { getImageUrl } from '../../lib/api.js'
import { i18n } from '../../lib/api.js'
import type { LocaleMetaDto } from '../../lib/api.js'
import { useI18n } from '../../lib/i18n.js'
import { menuLocaleOptions } from '../../lib/import-locale.js'
import { normalizeLocale } from '@self-restaurant/shared'
import { currencyFromMenuLocale } from '@self-restaurant/menu-import-core'
import { Button, toast } from '../../components/ui/index.js'
import { ImportReviewAllergens } from '../../components/ImportReviewAllergens.js'
import {
  dishEffectivePriceCents,
  ImportReviewPriceBadge,
  needsPriceConfirm,
} from '../../components/ImportReviewPriceBadge.js'
import { ImportReviewPriceInput } from '../../components/ImportReviewPriceInput.js'
import { ImportReviewVariants } from '../../components/ImportReviewVariants.js'
import {
  ImportReviewPriceFilter,
  productNeedsPriceConfirm,
} from '../../components/ImportReviewPriceFilter.js'
import { draftKey, withDecision, type DraftDecision } from '../../lib/allergens.js'
import { ReviewLocaleCurrency } from './ReviewLocaleCurrency.js'
import { ReviewChatNudge } from './ReviewChatNudge.js'
import { ReviewFooterActions } from './ReviewFooterActions.js'
import { ReviewServiceCharge } from './ReviewServiceCharge.js'
import { useReviewCurrency } from './useReviewCurrency.js'

// ─── Revisão + commit ─────────────────────────────────────────────────────────

/** Idioma-base do BCP-47 (pt-PT → pt) — comparação com o do restaurante. */
export const baseLang = (code: string) => code.trim().split(/[-_]/)[0]!.toLowerCase()

export type ReviewAction = 'ok' | 'fix' | 'remove'

export function ReviewPanel({ view }: { view: MenuImportView }) {
  const { t } = useI18n()
  const qc = useQueryClient()
  // rid estável por produto: flags/comentários sobrevivem a edição de nome,
  // espaços de OCR e itens duplicados (nome NÃO é chave confiável).
  // ATENÇÃO: inicializar direto de view.draft sem rid fazia TODO produto
  // compartilhar a chave undefined — marcar um grupo marcava todos.
  const withRids = (d: ImportDraft): ImportDraft => ({
    ...d,
    categories: d.categories.map((cat) => ({
      ...cat,
      products: cat.products.map((p) => ({ ...p, rid: p.rid ?? crypto.randomUUID() })),
    })),
  })

  const [draft, setDraft] = useState<ImportDraft | null>(() =>
    view.draft ? withRids(view.draft) : null,
  )
  // #428: normaliza na entrada — a API já manda base, mas uma resposta antiga
  // em cache pode trazer regional; as opções do seletor são as 14 bases
  const [locale, setLocale] = useState(() =>
    normalizeLocale(view.locale ?? view.draft?.locale ?? ''),
  )
  const restaurantLocale = normalizeLocale(view.restaurant_locale ?? '')
  // #299: escolha EXPLÍCITA do idioma pelo dono (botão ou seletor)
  const [localeConfirmed, setLocaleConfirmed] = useState(false)
  // #299 P6: idioma JÁ confirmado (coluna do servidor — resposta no chat com
  // topic 'locale' ou revisão anterior) — a revisão vem feita e o commit leva
  // confirm_locale sem novo clique
  const chatConfirmed = view.locale_confirmed === true
  const pickLocale = (code: string) => {
    setLocale(code)
    setLocaleConfirmed(true)
  }
  // #489 B (commit 1): estado e lógica do cartão de moeda extraídos para
  // useReviewCurrency — desestruturado com os MESMOS nomes; o JSX não muda
  const {
    currency,
    currencyConfirmed,
    currencyPending,
    currencyMismatchAlert,
    cardError,
    setCardError,
    currencyNeedsConfirm,
    commitFocusPending,
    setCommitFocusPending,
    pickCurrency,
    importErrorMessage,
    handleCommitCurrencyError,
    displayCurrency,
  } = useReviewCurrency({ view, locale, t, qc })
  const [applyInfo, setApplyInfo] = useState(Boolean(view.restaurant.name))
  const [mode, setMode] = useState<'append' | 'replace'>('append')
  // #474: filtro «Por confirmar» — só produtos/variantes que precisam de preço
  const [onlyPricePending, setOnlyPricePending] = useState(false)
  // UX r3 ponto 5: rids pendentes CONGELADOS ao ligar o filtro — a linha que
  // recebe preço continua visível (selo resolvido) até desligar o filtro
  const [frozenPendingRids, setFrozenPendingRids] = useState<ReadonlySet<string>>(new Set())
  // revisão local: category|name → ação + comentário (item) e comentários de grupo
  const [reviewState, setReviewState] = useState<Record<string, ReviewAction>>({})
  const [comments, setComments] = useState<Record<string, string>>({})
  const [groupComments, setGroupComments] = useState<Record<string, string>>({})
  // decisão pós-envio: itens sem revisão — modal em vez de mensagem no topo
  const [modal, setModal] = useState<{ open: boolean; pending: number }>({
    open: false,
    pending: 0,
  })
  // #325: decisões do dono sobre alergénios e ingredientes prováveis, por
  // categoria + nome (o rid muda quando o rascunho do servidor é adotado).
  // Vão no rascunho do commit; itens enviados para ajuste da IA perdem-nas
  // (o produto vai mudar, por isso volta a pedir confirmação).
  const [decisions, setDecisions] = useState<Record<string, DraftDecision>>({})
  const fixKeysRef = useRef<string[]>([])
  const dropFixedDecisions = () => {
    const keys = fixKeysRef.current
    if (keys.length === 0) return
    setDecisions((prev) => {
      const next = { ...prev }
      for (const k of keys) delete next[k]
      return next
    })
    fixKeysRef.current = []
  }

  const { data: locales = [] } = useQuery({
    queryKey: ['i18n-locales'],
    queryFn: i18n.locales,
    staleTime: 5 * 60_000,
  })

  // após a revisão ser aplicada no servidor (estado local limpo), adota o
  // rascunho atualizado — com rid para a próxima rodada de revisões.
  // Quando a IA termina (reviewing → extracted), a NOVA proposta é adotada
  // automaticamente para o dono re-revisar o que foi ajustado.
  const [syncFromServer, setSyncFromServer] = useState(false)
  const prevStatus = useRef(view.status)
  // PR-A.5: rastreia o que foi adotado (com rids) e o raw do servidor que o
  // gerou — para adotar o rascunho ENRIQUECIDO (extracted→extracted, o passo
  // assíncrono grava segundos depois) sem atropelar edição em curso
  const adoptedJson = useRef<string | null>(null)
  const adoptedServerJson = useRef<string | null>(null)
  // QA #347: nome ORIGINAL por rid — o servidor casa itens por
  // categoria+nome; um rename sem esta chave nunca era aplicado
  const originalNames = useRef<Map<string, string>>(new Map())
  const recordOriginals = (d: ImportDraft) => {
    for (const cat of d.categories) {
      for (const p of cat.products) {
        if (p.rid) originalNames.current.set(p.rid, p.name)
      }
    }
  }
  useEffect(() => {
    const adopt = (d: ImportDraft) => {
      const withR = withRids(d)
      setDraft(withR)
      adoptedJson.current = JSON.stringify(withR)
      adoptedServerJson.current = JSON.stringify(d)
      recordOriginals(withR)
    }
    const finishedReviewing = prevStatus.current === 'reviewing' && view.status === 'extracted'
    prevStatus.current = view.status
    // estado local intocado desde a última adoção (sem rids novos/edições)?
    const pristine =
      draft !== null &&
      adoptedJson.current !== null &&
      JSON.stringify(draft) === adoptedJson.current
    const serverChanged =
      view.draft !== null &&
      adoptedServerJson.current !== null &&
      JSON.stringify(view.draft) !== adoptedServerJson.current
    if ((syncFromServer || finishedReviewing) && view.draft) {
      adopt(view.draft)
      setSyncFromServer(false)
    } else if (!draft && view.draft) {
      adopt(view.draft)
      setLocale((l) => l || view.draft!.locale)
    } else if (adoptedServerJson.current === null && view.draft && draft) {
      // baseline da montagem (o useState já adotou o draft inicial — só
      // registra os refs para a detecção do enriquecimento funcionar)
      adoptedJson.current = JSON.stringify(draft)
      adoptedServerJson.current = JSON.stringify(view.draft)
      recordOriginals(draft)
    } else if (view.status === 'extracted' && serverChanged && pristine && view.draft) {
      // enriquecimento assíncrono chegou (badges ✦/▣ de sugestões) — adota
      // SÓ com o local intocado; com edição em curso o local fica e as
      // sugestões chegam na adoção reviewing→extracted após o submit
      adopt(view.draft)
    }
  }, [syncFromServer, view.status, view.draft]) // eslint-disable-line react-hooks/exhaustive-deps

  // comentário do grupo: vale para todos os itens do grupo que não têm
  // decisão própria; comentário de item tem prioridade sobre o do grupo
  const groupCommentOf = (category: string) => groupComments[category]?.trim() ?? ''

  const reviewMut = useMutation({
    mutationFn: () => {
      fixKeysRef.current = draft!.categories.flatMap((cat) =>
        cat.products
          .filter(
            (p) =>
              (reviewState[p.rid!] ?? (groupCommentOf(cat.name) !== '' ? 'fix' : undefined)) ===
              'fix',
          )
          .map((p) => draftKey(cat.name, p.name)),
      )
      return importsApi.submitReview(view.id, {
        items: draft!.categories.flatMap((cat) =>
          cat.products
            .filter(
              (p) =>
                (reviewState[p.rid!] !== undefined || groupCommentOf(cat.name) !== '') &&
                p.name.trim() !== '' &&
                cat.name.trim() !== '',
            )
            .map((p) => {
              const key = p.rid!
              const own = reviewState[key]
              const groupComment = groupCommentOf(cat.name)
              // sem decisão própria + comentário de grupo ⇒ precisa de ajuste
              // pelo comentário do grupo; decisão própria sempre vence
              const action: ReviewAction = own ?? (groupComment !== '' ? 'fix' : 'ok')
              const itemComment = comments[key]?.trim()
              const comment =
                action === 'fix'
                  ? itemComment !== '' && itemComment
                    ? itemComment
                    : groupComment
                  : undefined
              const original = p.rid ? originalNames.current.get(p.rid) : undefined
              const renamedFrom = original !== undefined && original.trim() !== p.name.trim()
              return {
                category: cat.name.trim().slice(0, 80),
                name: p.name.trim().slice(0, 120),
                action,
                ...(comment ? { comment: comment.slice(0, 400) } : {}),
                ...(renamedFrom ? { original_name: original!.trim().slice(0, 120) } : {}),
              }
            }),
        ),
        group_comments: Object.entries(groupComments)
          .filter(([category, c]) => c.trim() && category.trim() !== '')
          .map(([category, comment]) => ({
            category: category.trim().slice(0, 80),
            comment: comment.trim().slice(0, 400),
          })),
      })
    },
    onSuccess: (outcome) => {
      if (outcome.status !== 'pending') dropFixedDecisions()
      if (outcome.status === 'reviewing') {
        // IA aplicando em cadeia — progresso aparece no topo; nada é limpo
        setModal({ open: false, pending: 0 })
      } else if (outcome.status === 'pending') {
        // sobraram itens SEM decisão e SEM comentário de grupo — pergunta na
        // MODAL (sem limpar nada: o dono decide se certifica ou continua)
        setModal({ open: true, pending: outcome.pending })
      } else {
        toast.success(
          t('admin.import.review_applied_toast', {
            applied: String(outcome.applied),
            removed: String(outcome.removed),
          }),
        )
        // servidor agora é a fonte da verdade — limpa o estado local p/ liberar commit
        setReviewState({})
        setGroupComments({})
        setModal({ open: false, pending: 0 })
        // adota o rascunho DEPOIS do refetch (#278): antes, o sync rodava com
        // o draft velho e o novo nunca era adotado — Importar não aparecia
        void qc
          .invalidateQueries({ queryKey: ['menu-import', view.id] })
          .then(() => setSyncFromServer(true))
        return
      }
      void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const allOkMut = useMutation({
    mutationFn: () => importsApi.markAllReviewed(view.id),
    onSuccess: (outcome) => {
      if (outcome.status === 'reviewing') {
        setModal({ open: false, pending: 0 })
        return
      }
      if (outcome.status === 'applied') {
        toast.success(
          t('admin.import.review_applied_toast', {
            applied: String(outcome.applied),
            removed: String(outcome.removed),
          }),
        )
        setReviewState({})
        setGroupComments({})
        setModal({ open: false, pending: 0 })
        // #278: idem — só ressincroniza com o rascunho já revisado do servidor
        void qc
          .invalidateQueries({ queryKey: ['menu-import', view.id] })
          .then(() => setSyncFromServer(true))
        return
      }
      setModal({ open: false, pending: 0 })
      void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const commitMut = useMutation({
    mutationFn: () =>
      importsApi.commit(view.id, {
        locale,
        // crit. 8/P6: idioma já confirmado (coluna do servidor) dispensa o
        // flag; a escolha EXPLÍCITA aqui continua valendo e o commit grava
        // locale_confirmed_source 'review'
        confirm_locale: localeConfirmed || chatConfirmed,
        mode,
        apply_restaurant_info: applyInfo,
        // removes aplicados aqui mesmo quando não há revisão de servidor
        draft: {
          ...draft!,
          categories: draft!.categories
            .map((c) => ({
              ...c,
              products: c.products
                .filter((p) => actionOf(p) !== 'remove')
                .map((p) => withDecision(p, decisions[draftKey(c.name, p.name)])),
            }))
            .filter((c) => c.products.length > 0),
        },
      }),
    // #278: navigate(0) recarregava e perdia o import — refetch mostra import-done
    onSuccess: (out) => {
      setCardError(null) // #486: importou — erro inline sai
      // #489 (item 5): o COMMIT é a fonte única da contagem (mesma regra do
      // plano: prato pausado inteiro conta como prato; opções a 0 só dos
      // pratos que ficam no ar) — o toast anuncia a resposta e a tela de
      // conclusão (CommittedCard) mostra a linha que sobrevive a reload
      const pausedDishes = out.paused_dishes ?? 0
      const pausedOptions = out.paused_options ?? 0
      void qc.invalidateQueries({ queryKey: ['menu-import', view.id] })
      if (pausedDishes > 0) {
        toast.info(t('admin.import.paused_dishes_no_price', { n: String(pausedDishes) }))
      }
      if (pausedOptions > 0) {
        toast.info(t('admin.import.paused_options_no_price', { n: String(pausedOptions) }))
      }
    },
    onError: (e: Error) => {
      // corrida de moeda e 422 com chave i18n (#486) tratados no hook
      // (#489 B); o resto segue em toast (anunciado pelo Toaster, que tem
      // role="status"/aria-live desde o #474)
      if (handleCommitCurrencyError(e)) return
      toast.error(importErrorMessage(e))
    },
  })

  const allProducts = draft!.categories.flatMap((c) => c.products)
  const reviewed = allProducts.filter(
    (p) => reviewState[p.rid!] !== undefined || p.review !== undefined,
  ).length
  const pendingCount = allProducts.length - reviewed
  // #474 UX r2 (bloqueante): o filtro só ESCONDE linhas — as categorias e os
  // produtos guardam os índices REAIS do draft (ci/pi), senão updateProduct e
  // o renomear da categoria escreviam na cópia filtrada (prato errado/pratos
  // apagados). Nunca filtrar cópias: quem desaparece é a linha no render.
  // UX r3 (pontos 5/12): o conjunto de pendentes é CONGELADO quando o filtro
  // é ligado (uma linha com preço posto continua visível até desligar, sem
  // perder o foco) e os pratos marcados para remover ficam de fora.
  const setAction = (p: ImportDraftProduct, action: ReviewAction) =>
    setReviewState((prev) => ({ ...prev, [p.rid!]: action }))
  const actionOf = (p: ImportDraftProduct): ReviewAction | undefined =>
    reviewState[p.rid!] ?? (p.review === 'ok' ? 'ok' : p.review === 'fix' ? 'fix' : undefined)
  const visibleCategories = (draft?.categories ?? []).map((cat, ci) => ({
    cat,
    ci,
    products: cat.products
      .map((p, pi) => ({ p, pi }))
      .filter(
        (row) =>
          !onlyPricePending ||
          frozenPendingRids.has(row.p.rid ?? '') ||
          (productNeedsPriceConfirm(row.p) && actionOf(row.p) !== 'remove'),
      ),
  }))
  const pricePendingCount = allProducts.filter(
    (p) => productNeedsPriceConfirm(p) && actionOf(p) !== 'remove',
  ).length

  // remover é uma AÇÃO de revisão (vai no payload como 'remove') — se o
  // item só sumisse do draft local, o servidor nunca sabia e ele voltava
  // como "pendente" fantasma; clicar de novo restaura
  const removeProduct = (p: ImportDraftProduct) => {
    if (actionOf(p) === 'remove') {
      setReviewState((prev) => {
        const next = { ...prev }
        delete next[p.rid!]
        return next
      })
    } else {
      setReviewState((prev) => ({ ...prev, [p.rid!]: 'remove' }))
    }
  }

  const updateProduct = (ci: number, pi: number, patch: Partial<ImportDraftProduct>) =>
    setDraft((d) => {
      if (!d) return d
      const categories = [...d.categories]
      const cat = { ...categories[ci]!, products: [...categories[ci]!.products] }
      cat.products[pi] = { ...cat.products[pi]!, ...patch }
      categories[ci] = cat
      return { ...d, categories }
    })

  // #299: idioma ≠ do restaurante (ou não detectado) só entra confirmado —
  // exceto quando o dono JÁ confirmou (coluna do servidor, chat ou revisão)
  const localeNeedsConfirm =
    !locale.trim() ||
    (!chatConfirmed &&
      !!restaurantLocale &&
      baseLang(locale) !== restaurantLocale &&
      !localeConfirmed)
  const canCommit =
    locale.trim().length >= 2 &&
    !localeNeedsConfirm &&
    !currencyNeedsConfirm &&
    draft!.categories.length > 0
  // M1/B2 (item 5): foco no «Importar» na 1.ª renderização em que ele ativa
  // (o focus() num botão disabled não faz nada)
  useEffect(() => {
    if (!commitFocusPending || commitMut.isPending) return
    const button = document.getElementById('import-commit') as HTMLButtonElement | null
    if (button && !button.disabled) {
      button.focus()
      setCommitFocusPending(false)
    }
  }, [commitFocusPending, commitMut.isPending, canCommit, setCommitFocusPending])

  if (!draft) return null
  // crit. 9: texto do Menu (nomes/descrições) no idioma DELE — árabe em RTL
  // mesmo com o painel noutro idioma
  const menuTextProps = {
    lang: locale || undefined,
    dir: locale === 'ar' ? ('rtl' as const) : ('ltr' as const),
  }
  // #304: opções = catálogo ativo + idioma detectado / do restaurante quando
  // fora dele — o seletor nunca mais mostra "—" com idioma
  const localeOptions = menuLocaleOptions(locales as LocaleMetaDto[], [
    locale,
    view.draft?.locale,
    restaurantLocale,
  ])
  // revisão não enviada: flags locais, comentários de grupo ou itens pendentes —
  // o commit SÓ aparece depois que a revisão vai ao servidor (e a IA aplica)
  // aplicação pela IA em andamento (status reviewing): progresso VIVO do
  // rascunho do SERVIDOR (a corrente persiste a cada lote) — o estado local
  // congela no envio e fazia a barra parecer travada
  const liveProducts = (view.draft?.categories ?? []).flatMap((c) => c.products)
  const fixDone = liveProducts.filter((p) => p.fixed).length
  const fixRemaining = liveProducts.filter((p) => p.review === 'fix' && !p.fixed).length
  const isReviewing = view.status === 'reviewing'

  // trabalho que EXIGE a IA: ao menos um item marcado p/ ajuste (com
  // comentário próprio ou de grupo). Sem isso — tudo ok/removido — a ida ao
  // servidor era um passo morto: o botão correto é IMPORTAR direto
  const hasFixWork =
    allProducts.some((p) => reviewState[p.rid!] === 'fix') ||
    Object.values(groupComments).some((c) => c.trim().length > 0)
  const hasUnsubmittedReview = !isReviewing && (hasFixWork || pendingCount > 0)
  const lastQuestion = [...view.messages].reverse().find((m) => m.kind === 'question')
  const showChatNudge = view.awaiting === 'input' && lastQuestion !== null

  return (
    <div className="space-y-5">
      {/* chat de certificação (pergunta do assistente + quick replies) */}
      {showChatNudge && (
        <ReviewChatNudge
          content={lastQuestion!.content}
          quickReplies={lastQuestion!.quick_replies}
          onSend={(text) =>
            void importsApi
              .message(view.id, text)
              .then(() => qc.invalidateQueries({ queryKey: ['menu-import', view.id] }))
          }
          onAllOk={() => allOkMut.mutate()}
        />
      )}

      {/* IA aplicando os comentários — progresso real, item a item */}
      {isReviewing && (
        <div className="rounded-2xl border-2 border-indigo-300 bg-indigo-50 p-4 space-y-2">
          <p className="text-sm font-bold text-indigo-900">
            {t('admin.import.reviewing_title', {
              done: String(fixDone),
              total: String(fixDone + fixRemaining),
            })}
          </p>
          <div className="h-1.5 rounded-full bg-indigo-100 overflow-hidden">
            <div
              className="h-full bg-indigo-500 transition-all"
              style={{
                width: `${
                  fixDone + fixRemaining > 0 ? (fixDone / (fixDone + fixRemaining)) * 100 : 0
                }%`,
              }}
            />
          </div>
          <p className="text-xs text-indigo-700">{t('admin.import.reviewing_hint')}</p>
        </div>
      )}

      {/* progresso da revisão (o envio fica embaixo, junto da lista) */}
      <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-2">
        <p className="text-sm font-bold text-gray-900">
          {t('admin.import.fix_progress', {
            done: String(reviewed),
            total: String(allProducts.length),
          })}
        </p>
        <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${allProducts.length ? (reviewed / allProducts.length) * 100 : 0}%` }}
          />
        </div>
        {pendingCount > 0 && (
          <p className="text-xs text-amber-600">{t('admin.import.review_pending_warn')}</p>
        )}
        {/* #474: atalho para o que falta preço (produto ou variante a 0,00/PSM) */}
        <div>
          <ImportReviewPriceFilter
            active={onlyPricePending}
            count={pricePendingCount}
            onToggle={() => {
              // UX r3 ponto 5: ao LIGAR, congela os pendentes AGORA (a linha
              // que receber preço continua visível até desligar o filtro)
              setFrozenPendingRids(
                onlyPricePending
                  ? new Set()
                  : new Set(
                      allProducts
                        .filter((p) => productNeedsPriceConfirm(p) && actionOf(p) !== 'remove')
                        .map((p) => p.rid ?? ''),
                    ),
              )
              setOnlyPricePending(!onlyPricePending)
            }}
          />
        </div>
      </div>

      {/* #299: idioma (com confirmação #301/#304 e par Menu × restaurante,
          crit. 9) + moeda EDITÁVEL com confirmação e aviso (crit. 7) —
          estado aqui, apresentação em ReviewLocaleCurrency */}
      <ReviewLocaleCurrency
        locale={locale}
        restaurantLocale={restaurantLocale}
        localeOptions={localeOptions}
        localeNeedsConfirm={localeNeedsConfirm}
        onPickLocale={pickLocale}
        currency={currency}
        currencyDetected={view.currency_detected !== false}
        currencyNeedsConfirm={currencyNeedsConfirm}
        currencySuggested={view.currency_suggested ?? currencyFromMenuLocale(locale)}
        restaurantCurrency={view.restaurant_currency ?? null}
        onPickCurrency={pickCurrency}
        currencyPending={currencyPending}
        currencyConfirmed={currencyConfirmed}
        mismatchAlert={currencyMismatchAlert}
        error={cardError}
      />

      {/* restaurante */}
      {(view.restaurant.name || view.restaurant.logo_url) && (
        <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3">
          <p className="text-sm font-bold text-gray-900">{t('admin.import.restaurant_info')}</p>
          {view.restaurant.logo_url && (
            <img
              src={getImageUrl(draft.restaurant.logo_r2_key ?? '') || view.restaurant.logo_url}
              alt="logo"
              className="h-16 rounded-xl border border-gray-100"
            />
          )}
          <p className="text-sm text-gray-700">
            <b>{view.restaurant.name}</b>
            {view.restaurant.about ? ` — ${view.restaurant.about}` : ''}
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              data-testid="import-review-apply-info"
              checked={applyInfo}
              onChange={(e) => setApplyInfo(e.target.checked)}
            />
            {t('admin.import.apply_info')}
          </label>
        </div>
      )}

      {/* categorias/produtos com revisão */}
      <p
        className="text-[11px] text-gray-500 bg-amber-50/60 border border-amber-100 rounded-lg px-3 py-2"
        data-testid="import-review-confirm-note"
      >
        ✦ {t('admin.import.review_confirm_note')}
      </p>

      {/* #321: taxa de serviço/coperto lida no Menu — Intl na moeda do
          import (sugestão, nunca cobrança; apresentação em ReviewServiceCharge).
          #486: o país compõe o locale do Intl (formato do país do restaurante) */}
      <ReviewServiceCharge
        charges={draft.service_charges}
        currency={view.restaurant_currency}
        country={view.restaurant_country ?? null}
      />

      {/* UX r2: categorias SEM linha visível não rendecem cartão vazio —
          os índices reais (ci/pi) ficam guardados nas linhas acima */}
      {visibleCategories
        .filter((entry) => entry.products.length > 0)
        .map(({ cat, ci, products: catProducts }) => {
          const catReviewed = cat.products.filter(
            (p) => reviewState[p.rid!] !== undefined || p.review !== undefined,
          ).length
          return (
            <div
              key={ci}
              data-testid={`import-review-category-${ci}`}
              className="rounded-2xl border border-gray-200 bg-white p-4 space-y-3"
            >
              <div className="flex items-center gap-2">
                <input
                  data-testid={`import-review-category-name-${ci}`}
                  {...menuTextProps}
                  value={cat.name}
                  onChange={(e) =>
                    setDraft((d) => {
                      if (!d) return d
                      const categories = [...d.categories]
                      categories[ci] = { ...cat, name: e.target.value }
                      return { ...d, categories }
                    })
                  }
                  className="font-bold text-gray-900 bg-transparent border-b border-transparent focus:border-gray-300 outline-none text-sm flex-1"
                />
                <span className="text-xs text-gray-400">
                  {catReviewed}/{cat.products.length}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setReviewState((prev) => {
                      const next = { ...prev }
                      catProducts.forEach(({ p }) => {
                        next[p.rid!] = 'ok'
                      })
                      return next
                    })
                  }
                  className="rounded-full border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs px-3 py-1 hover:bg-emerald-100"
                >
                  ✓ {t('admin.import.review_mark_all')}
                </button>
                <button
                  type="button"
                  disabled={groupCommentOf(cat.name) === ''}
                  title={
                    groupCommentOf(cat.name) === ''
                      ? t('admin.import.review_group_hint')
                      : undefined
                  }
                  onClick={() =>
                    setReviewState((prev) => {
                      const next = { ...prev }
                      catProducts.forEach(({ p }) => {
                        next[p.rid!] = 'fix'
                      })
                      return next
                    })
                  }
                  className="rounded-full border border-amber-300 bg-amber-50 text-amber-700 text-xs px-3 py-1 hover:bg-amber-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  ✎ {t('admin.import.review_group_fix')}
                </button>
              </div>

              {/* comentário do grupo */}
              <textarea
                data-testid={`import-review-category-comment-${ci}`}
                value={groupComments[cat.name] ?? ''}
                onChange={(e) =>
                  setGroupComments((prev) => ({ ...prev, [cat.name]: e.target.value }))
                }
                rows={2}
                placeholder={t('admin.import.review_group_ph')}
                className="w-full text-xs text-gray-600 bg-amber-50 border border-amber-100 rounded-lg p-2 resize-none"
              />

              {catProducts.map(({ p, pi }) => {
                const action = actionOf(p)
                return (
                  <div
                    data-testid={`import-review-product-${ci}-${pi}`}
                    key={p.rid ?? pi}
                    className={`rounded-xl border p-3 space-y-2 ${
                      action === 'ok'
                        ? 'border-emerald-200 bg-emerald-50/40'
                        : action === 'fix'
                          ? 'border-amber-300 bg-amber-50/40'
                          : action === 'remove'
                            ? 'border-red-200 bg-red-50/40 opacity-60'
                            : 'border-gray-100'
                    }`}
                  >
                    <div className="flex gap-2">
                      {p.photo_r2_key && (
                        <img
                          src={getImageUrl(p.photo_r2_key)}
                          alt={p.name}
                          className="w-14 h-14 rounded-lg object-cover border border-gray-100"
                        />
                      )}
                      <div className="flex-1 min-w-0 space-y-1">
                        <input
                          data-testid={`import-review-product-name-${ci}-${pi}`}
                          {...menuTextProps}
                          value={p.name}
                          title={p.name}
                          onChange={(e) => updateProduct(ci, pi, { name: e.target.value })}
                          className="w-full text-sm font-semibold bg-transparent border-b border-transparent focus:border-gray-300 outline-none"
                        />
                        {/* #320: 2.ª língua impressa no Menu bilíngue — vira
                          linha de tradução no commit, não descrição */}
                        {(p.translations ?? []).length > 0 && (
                          <div className="flex gap-1 flex-wrap">
                            {(p.translations ?? []).map((tr) => (
                              <span
                                key={tr.locale}
                                data-testid={`import-review-product-translation-${ci}-${pi}`}
                                title={tr.name ?? tr.description ?? ''}
                                className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2 py-0.5"
                              >
                                {t('admin.import.translation_found', { locale: tr.locale })}
                              </span>
                            ))}
                          </div>
                        )}
                        <textarea
                          data-testid={`import-review-product-description-${ci}-${pi}`}
                          {...menuTextProps}
                          value={p.description ?? ''}
                          onChange={(e) => updateProduct(ci, pi, { description: e.target.value })}
                          rows={2}
                          placeholder={t('admin.import.description')}
                          className="w-full text-xs text-gray-600 bg-gray-50 rounded p-1.5 resize-none"
                        />
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {/* #278: moeda da fonte junto do preço (antes só o número) */}
                        {/* M1/8: mostra a moeda que será GRAVADA (restaurante) */}
                        {view.restaurant_currency && (
                          <span
                            data-testid={`import-review-product-currency-${ci}-${pi}`}
                            className="text-[10px] uppercase text-gray-600"
                          >
                            {view.restaurant_currency}
                          </span>
                        )}
                        {/* #474: sem reformatação a cada tecla; rótulo acessível e
                          ligado ao selo «Confirmar preço» por aria-describedby.
                          QA r3 (item 2c): com variantes o campo é SÓ DE
                          LEITURA — mostra o base DERIVADO (mínimo das opções
                          com preço, a MESMA regra do commit) e NÃO grava
                          price_cents (o valor escrito era ignorado pelo
                          display/commit e «escorregava» sem explicação) */}
                        <ImportReviewPriceInput
                          testId={`import-review-product-price-${ci}-${pi}`}
                          cents={dishEffectivePriceCents(p)}
                          currency={displayCurrency}
                          label={t('admin.import.price_of', { name: p.name })}
                          {...(needsPriceConfirm(p)
                            ? {
                                describedBy: `import-review-price-confirm-${p.rid ?? `${ci}-${pi}`}`,
                              }
                            : {})}
                          {...(p.variants.length > 0
                            ? {
                                readOnly: true,
                                hint: t('admin.import.price_base_from_variants'),
                                onCommit: () => {},
                              }
                            : {
                                onCommit: (cents: number) =>
                                  updateProduct(ci, pi, { price_cents: cents }),
                              })}
                        />
                        {p.calories_kcal !== undefined && (
                          <input
                            data-testid={`import-review-product-kcal-${ci}-${pi}`}
                            title="kcal"
                            aria-label="kcal"
                            value={String(p.calories_kcal)}
                            onChange={(e) => {
                              const n = Math.round(Number(e.target.value.replace(',', '.')))
                              setDraft((d) => {
                                if (!d) return d
                                const categories = [...d.categories]
                                const products = [...categories[ci]!.products]
                                const target = { ...products[pi]! }
                                if (Number.isFinite(n) && n > 0) target.calories_kcal = n
                                else delete target.calories_kcal
                                products[pi] = target
                                categories[ci] = { ...categories[ci]!, products }
                                return { ...d, categories }
                              })
                            }}
                            className="w-14 text-[11px] text-right rounded border border-gray-200 px-1.5 py-1"
                          />
                        )}
                        <button
                          type="button"
                          data-testid={`import-review-product-remove-${ci}-${pi}`}
                          onClick={() => removeProduct(p)}
                          className="text-xs text-red-500 hover:underline"
                        >
                          {action === 'remove'
                            ? `↩ ${t('admin.import.review_undo_remove')}`
                            : t('admin.import.remove')}
                        </button>
                      </div>
                    </div>

                    {/* flags de revisão */}
                    <div className="flex flex-wrap items-center gap-2">
                      {/* #299/#474: preço não reconhecido (0,00/PSM) — «Confirmar
                        preço» + texto impresso + «fica pausado»; sai quando o
                        dono põe o preço */}
                      <ImportReviewPriceBadge
                        product={p}
                        id={`import-review-price-confirm-${p.rid ?? `${ci}-${pi}`}`}
                      />
                      {p.fixed && (
                        <span className="rounded-full bg-indigo-50 text-indigo-700 text-xs px-2 py-0.5">
                          ✨ {t('admin.import.review_fixed')}
                        </span>
                      )}
                      {action === 'remove' && (
                        <span className="rounded-full bg-red-100 text-red-700 text-xs px-2 py-0.5 line-through">
                          {t('admin.import.review_item_removed')}
                        </span>
                      )}
                      <button
                        type="button"
                        data-testid={`import-review-product-ok-${ci}-${pi}`}
                        onClick={() => setAction(p, 'ok')}
                        className={`rounded-full text-xs px-3 py-1 min-h-11 border ${
                          action === 'ok'
                            ? 'bg-emerald-500 border-emerald-500 text-white'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-emerald-50'
                        }`}
                      >
                        ✓ {t('admin.import.review_item_ok')}
                      </button>
                      <button
                        type="button"
                        data-testid={`import-review-product-fix-${ci}-${pi}`}
                        onClick={() => setAction(p, 'fix')}
                        className={`rounded-full text-xs px-3 py-1 min-h-11 border ${
                          action === 'fix'
                            ? 'bg-amber-500 border-amber-500 text-pratzu-aubergine'
                            : 'bg-white border-gray-200 text-gray-600 hover:bg-amber-50'
                        }`}
                      >
                        ✎ {t('admin.import.review_item_fix')}
                      </button>
                      {action === undefined && (
                        <span className="rounded-full bg-gray-100 text-gray-500 text-xs px-2 py-0.5">
                          {t('admin.import.review_pending')}
                        </span>
                      )}
                    </div>

                    {/* comentário do item quando precisa de ajuste */}
                    {action === 'fix' && (
                      <textarea
                        data-testid={`import-review-product-comment-${ci}-${pi}`}
                        value={comments[p.rid!] ?? p.review_comment ?? ''}
                        onChange={(e) =>
                          setComments((prev) => ({ ...prev, [p.rid!]: e.target.value }))
                        }
                        rows={2}
                        placeholder={t('admin.import.review_comment_ph')}
                        className="w-full text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded-lg p-2 resize-none"
                      />
                    )}

                    {(p.variants.length > 0 || p.extras.length > 0 || p.ingredients.length > 0) && (
                      <div className="flex flex-wrap gap-1">
                        {/* #474: variante sem preço mostra o selo e o campo para
                          pôr o preço — o commit vai pausá-la tal como o produto */}
                        <ImportReviewVariants
                          variants={p.variants}
                          ci={ci}
                          pi={pi}
                          currency={displayCurrency}
                          onVariantsChange={(variants) => updateProduct(ci, pi, { variants })}
                        />
                        {p.extras.map((e, ei) => (
                          <span
                            key={ei}
                            className="rounded-full bg-amber-50 text-amber-700 text-xs px-2 py-0.5"
                            title={t('admin.import.extras')}
                          >
                            + {e.name} · {(e.price_cents / 100).toFixed(2)}
                          </span>
                        ))}
                        {p.ingredients.map((ing, ii) => (
                          <span
                            key={ii}
                            className="rounded-full bg-gray-100 text-gray-600 text-xs px-2 py-0.5"
                            title={t('admin.import.ingredients')}
                          >
                            {ing}
                          </span>
                        ))}
                        {p.has_photo && !p.photo_r2_key && (
                          <span className="rounded-full bg-gray-100 text-gray-500 text-xs px-2 py-0.5">
                            📷 {t('admin.import.has_photo')}
                          </span>
                        )}
                      </div>
                    )}

                    {/* #325: alergénios e ingredientes prováveis (mesmos chips do produto) */}
                    {action !== 'remove' && (
                      <ImportReviewAllergens
                        product={withDecision(p, decisions[draftKey(cat.name, p.name)])}
                        decision={decisions[draftKey(cat.name, p.name)]}
                        onChange={(d) =>
                          setDecisions((prev) => ({ ...prev, [draftKey(cat.name, p.name)]: d }))
                        }
                        testId={`${ci}-${pi}`}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}

      {draft.categories.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-4">{t('admin.import.no_items')}</p>
      )}

      {/* UX r2 ponto 4: com o filtro ligado e nada por confirmar, estado
          próprio (o «Nenhum item reconhecido» parecia falha do import) */}
      {onlyPricePending && pricePendingCount === 0 && (
        <div
          data-testid="import-review-nothing-to-confirm"
          className="flex items-center justify-between rounded-2xl border border-gray-200 bg-white p-4"
        >
          <p className="text-sm text-gray-500">{t('admin.import.nothing_to_confirm')}</p>
          <button
            type="button"
            data-testid="import-review-see-all"
            onClick={() => setOnlyPricePending(false)}
            className="rounded-full border border-gray-200 bg-white text-gray-600 text-xs px-3 min-h-11 hover:bg-gray-50"
          >
            {t('admin.import.see_all')}
          </button>
        </div>
      )}

      {/* AÇÃO PRINCIPAL — sempre embaixo, junto do fim da lista:
          revisão não enviada ⇒ SÓ o botão de revisão; limpa ⇒ commit */}
      <ReviewFooterActions
        hasUnsubmittedReview={hasUnsubmittedReview}
        pendingCount={pendingCount}
        reviewPending={reviewMut.isPending}
        onSubmitReview={() => reviewMut.mutate()}
        mode={mode}
        onModeChange={setMode}
        canCommit={canCommit}
        currencyNeedsConfirm={currencyNeedsConfirm}
        commitPending={commitMut.isPending}
        onCommit={() => commitMut.mutate()}
      />

      {/* MODAL: sobraram itens sem revisão — certifica agora ou continua
          (nada é limpo; os comentários escritos permanecem) */}
      {modal.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setModal({ open: false, pending: 0 })}
        >
          <div
            data-testid="import-review-modal"
            className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900">
              {t('admin.import.review_modal_title')}
            </h3>
            <p className="text-sm text-gray-600">
              {t('admin.import.review_modal_body', { n: String(modal.pending) })}
            </p>
            <div className="flex flex-col gap-2">
              <Button
                loading={allOkMut.isPending}
                data-testid="import-review-modal-all-ok"
                onClick={() => allOkMut.mutate()}
              >
                {t('admin.import.review_modal_all_ok')}
              </Button>
              <Button
                variant="secondary"
                data-testid="import-review-modal-keep"
                onClick={() => setModal({ open: false, pending: 0 })}
              >
                {t('admin.import.review_modal_keep')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
